import React from 'react';
import { CheckCircle2, Sparkles, Award, AlertTriangle, Star, Wallet } from 'lucide-react';
import { Button } from '../ui/button';
import { computeBrokerProposal, formatCurrency } from '../../lib/proposal/brokerCalc';

/* Slide deck shared by `purchase` + `refinance` proposal types.
 * Each slide receives the same props as ConsolidationSlides:
 *   - payload, calc, clientName, brokerName, readonly, accepted, onAccept
 * Variations between purchase / refinance are driven by `payload.loanPurpose`
 * so we don't need two near-identical files. */

const Container = ({ children, dark = false }) => (
  <div
    className={`relative w-full min-h-full flex flex-col px-5 sm:px-10 md:px-16 overflow-x-hidden ${
      dark
        ? 'bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950 text-white'
        : 'bg-gradient-to-br from-slate-50 via-white to-emerald-50 text-slate-900'
    }`}
  >
    <div
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 ${dark ? 'opacity-50' : 'opacity-30'}`}
      style={{
        backgroundImage: dark
          ? 'radial-gradient(ellipse at top right, rgba(16,185,129,0.18), transparent 60%), radial-gradient(ellipse at bottom left, rgba(59,130,246,0.10), transparent 50%)'
          : 'radial-gradient(ellipse at top right, rgba(16,185,129,0.12), transparent 50%)',
      }}
    />
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 mix-blend-overlay opacity-[0.04]"
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 1 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
      }}
    />
    <div className="relative w-full max-w-6xl mx-auto py-8 my-auto">
      {children}
    </div>
  </div>
);

const Eyebrow = ({ children, dark = true }) => (
  <p className={`text-xs uppercase tracking-[0.2em] font-bold mb-3 ${dark ? 'text-emerald-400' : 'text-emerald-600'}`}>{children}</p>
);

const isRefi = (p) => (p?.loanPurpose === 'refinance');
const proposalLabel = (p) => (isRefi(p) ? 'Refinance Proposal' : 'Purchase Proposal');

// ─────────────────── Slide 1: Cover ───────────────────
const SlideCover = ({ payload, clientName, brokerName }) => (
  <Container dark>
    <Eyebrow>{proposalLabel(payload)}</Eyebrow>
    <h1 className="text-4xl md:text-6xl font-bold leading-tight">
      {isRefi(payload) ? (
        <>A smarter <span className="text-emerald-400">refinance</span> structure.</>
      ) : (
        <>Your home loan, <span className="text-emerald-400">structured</span>.</>
      )}
    </h1>
    {clientName && (
      <p className="mt-6 text-lg md:text-xl text-slate-300">
        Prepared for <span className="text-white font-semibold">{clientName}</span>
        {brokerName ? <> by <span className="text-white font-semibold">{brokerName}</span></> : null}.
      </p>
    )}
    <p className="mt-2 text-sm text-slate-400 max-w-xl">
      A live walk-through of your funds breakdown, three lender options, and our recommended path forward.
    </p>
    <div className="mt-10 flex items-center gap-2 text-xs text-slate-500">
      <Sparkles className="h-4 w-4 text-emerald-400" />
      Live, interactive — your numbers, your strategy.
    </div>
  </Container>
);

// ─────────────────── NEW Slide: Current Situation ───────────────────
// Purchase: shows the property + deposit + target loan + LVR.
// Refinance: shows current loan balance + rate + remaining term + total debts being consolidated.
const SlideCurrentSituation = ({ payload, calc }) => {
  const refi = isRefi(payload);
  const fr = payload?.fundsRequired || {};
  const fa = payload?.fundsAvailable || {};
  const debts = Array.isArray(payload?.debts) ? payload.debts.filter((d) => Number(d?.amount) > 0) : [];
  const totalDebts = debts.reduce((sum, d) => sum + (Number(d.amount) || 0), 0);
  const purchasePrice = Number(fr.purchasePrice) || 0;
  const loanAmount = Number(fa.loanAmount) || 0;
  const lvr = purchasePrice ? (loanAmount / purchasePrice) * 100 : null;

  return (
    <Container>
      <Eyebrow dark={false}>{refi ? "Where you're at today" : 'The Property'}</Eyebrow>
      <h2 className="text-3xl md:text-5xl font-bold mb-2">{refi ? 'Your current position' : 'Your purchase, today.'}</h2>
      <p className="text-slate-600 mb-6 text-sm max-w-2xl">
        {refi
          ? "Here's what we're consolidating + restructuring — every figure pulled from the data your broker entered."
          : "Here's the picture as it stands — purchase price, contributions, and the loan we're putting in place."}
      </p>

      {refi ? (
        <>
          <div className="grid md:grid-cols-3 gap-4 mb-5">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
              <p className="text-xs text-slate-500">Current loan balance</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{formatCurrency(payload?.currentLoanBalance)}</p>
              <p className="text-[10px] text-slate-500 mt-1">To be refinanced</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
              <p className="text-xs text-slate-500">New loan balance</p>
              <p className="text-2xl font-bold text-emerald-700 mt-1">{formatCurrency(payload?.newLoanBalance || loanAmount)}</p>
              <p className="text-[10px] text-slate-500 mt-1">After refinance</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
              <p className="text-xs text-slate-500">Debts being consolidated</p>
              <p className="text-2xl font-bold text-rose-600 mt-1">{formatCurrency(totalDebts)}</p>
              <p className="text-[10px] text-slate-500 mt-1">{debts.length} debt{debts.length === 1 ? '' : 's'} folded into the new loan</p>
            </div>
          </div>
          {debts.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
              <p className="text-[11px] uppercase tracking-wide font-bold text-slate-500 mb-2">Debt schedule</p>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-slate-500">
                    <th className="text-left py-1.5 font-semibold text-xs">Type</th>
                    <th className="text-left py-1.5 font-semibold text-xs">Lender / detail</th>
                    <th className="text-right py-1.5 font-semibold text-xs">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {debts.map((d, i) => (
                    <tr key={i} className="border-b border-slate-100">
                      <td className="py-1.5 text-slate-700 text-xs">{d.type || 'Debt'}</td>
                      <td className="py-1.5 text-slate-600 text-xs">{d.name || '—'}</td>
                      <td className="py-1.5 text-right font-medium">{formatCurrency(d.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
            <p className="text-xs text-slate-500">Purchase price</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{formatCurrency(purchasePrice)}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
            <p className="text-xs text-slate-500">Loan amount</p>
            <p className="text-2xl font-bold text-emerald-700 mt-1">{formatCurrency(loanAmount)}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
            <p className="text-xs text-slate-500">Deposit + contributions</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{formatCurrency((Number(fa.deposit)||0) + (Number(fa.gift)||0) + (Number(fa.fhog)||0) + (Number(fa.otherContributions)||0))}</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
            <p className="text-xs text-slate-500">LVR</p>
            <p className={`text-2xl font-bold mt-1 ${lvr != null && lvr > 80 ? 'text-rose-600' : 'text-emerald-700'}`}>{lvr != null ? `${lvr.toFixed(1)}%` : '—'}</p>
            <p className="text-[10px] text-slate-500 mt-1">{lvr != null && lvr > 80 ? 'Above 80% — LMI may apply' : 'At or below 80%'}</p>
          </div>
        </div>
      )}
    </Container>
  );
};

// ─────────────────── NEW Slide: What We're Proposing ───────────────────
// Mirror of consolidation deck's "What We're Proposing" — broker-pitch intro
// with two clearly numbered sections.
const SlideProposalIntro = ({ payload, calc }) => {
  const refi = isRefi(payload);
  const rec = calc.recommended;
  const recRate = rec ? Number(rec.rate || 0) : 0;
  const lenders = Array.isArray(payload?.lenders) ? payload.lenders : [];
  const maxRate = lenders.length ? Math.max(...lenders.map((l) => Number(l.rate) || 0)) : recRate;
  const rateDelta = Math.max(0, maxRate - recRate);

  return (
    <Container>
      <Eyebrow dark={false}>What We're Proposing</Eyebrow>
      <h2 className="text-3xl md:text-5xl font-bold mb-2">Here's the plan, in two parts.</h2>
      <p className="text-slate-600 mb-6 text-sm max-w-2xl">A clear, broker-recommended structure designed around your numbers — not a generic template.</p>

      {/* 1. The Proposal */}
      <div className="bg-white rounded-2xl border-2 border-blue-300 shadow-sm p-5 mb-4">
        <div className="flex items-baseline gap-3 mb-2">
          <span className="text-3xl font-bold text-blue-600">1.</span>
          <h3 className="text-xl font-bold text-slate-900">The Proposal</h3>
        </div>
        <div className="space-y-2 text-sm text-slate-700 leading-relaxed">
          {refi ? (
            <>
              <p>Refinance your existing <strong>{formatCurrency(payload?.currentLoanBalance)}</strong> home loan into a new <strong>{formatCurrency(calc.loanAmount)}</strong> facility with <strong>{rec ? rec.name : 'our recommended lender'}</strong> at <strong className="text-emerald-700">{recRate.toFixed(2)}%</strong> ({rec?.type || 'Variable'} · {rec?.repayment || 'P&I'}).</p>
              <p>Roll any high-interest debt being consolidated into the same facility so you have a single low-rate repayment.</p>
              <p>Activate the structured features ({rec?.features?.offset ? 'offset' : ''}{rec?.features?.offset && rec?.features?.redraw ? ', ' : ''}{rec?.features?.redraw ? 'redraw' : ''}{(rec?.features?.offset || rec?.features?.redraw) && rec?.features?.extraPayments ? ', ' : ''}{rec?.features?.extraPayments ? 'extra payments' : ''}) so cash sitting in your accounts works against the loan balance.</p>
            </>
          ) : (
            <>
              <p>Settle your <strong>{formatCurrency(Number((payload?.fundsRequired||{}).purchasePrice))}</strong> purchase with a <strong>{formatCurrency(calc.loanAmount)}</strong> home loan from <strong>{rec ? rec.name : 'our recommended lender'}</strong> at <strong className="text-emerald-700">{recRate.toFixed(2)}%</strong> ({rec?.type || 'Variable'} · {rec?.repayment || 'P&I'}).</p>
              <p>Deposit + contributions cover the difference, leaving funds available to handle stamp duty, legal fees, and any LMI.</p>
              <p>Activate the structured features ({rec?.features?.offset ? 'offset' : ''}{rec?.features?.offset && rec?.features?.redraw ? ', ' : ''}{rec?.features?.redraw ? 'redraw' : ''}{(rec?.features?.offset || rec?.features?.redraw) && rec?.features?.extraPayments ? ', ' : ''}{rec?.features?.extraPayments ? 'extra payments' : ''}) so every dollar of cash flow attacks the loan balance.</p>
            </>
          )}
        </div>
      </div>

      {/* 2. What You'll Save / Reduce */}
      <div className="bg-white rounded-2xl border-2 border-emerald-300 shadow-sm p-5">
        <div className="flex items-baseline gap-3 mb-3">
          <span className="text-3xl font-bold text-emerald-600">2.</span>
          <h3 className="text-xl font-bold text-slate-900">{refi ? "What You'll Reduce" : "What You'll Save"}</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <p className="text-[10px] uppercase tracking-wide font-bold text-emerald-700">Recommended rate</p>
            <p className="text-2xl font-bold text-slate-900 mt-1 proposal-pulse-number">{recRate.toFixed(2)}%</p>
            <p className="text-[10px] text-slate-500 mt-0.5">{rec?.type || 'Variable'} · {rec?.repayment || 'P&I'}</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-[10px] uppercase tracking-wide font-bold text-blue-700">Vs highest option</p>
            <p className="text-2xl font-bold text-slate-900 mt-1 proposal-pulse-number">{rateDelta > 0 ? `${rateDelta.toFixed(2)}%` : '—'}</p>
            <p className="text-[10px] text-slate-500 mt-0.5">cheaper than the priciest lender on your shortlist</p>
          </div>
          <div className="bg-violet-50 border border-violet-200 rounded-lg p-3">
            <p className="text-[10px] uppercase tracking-wide font-bold text-violet-700">Monthly repayment</p>
            <p className="text-2xl font-bold text-slate-900 mt-1 proposal-pulse-number">{formatCurrency(calc.repayments.monthly)}</p>
            <p className="text-[10px] text-slate-500 mt-0.5">at {recRate.toFixed(2)}% over 360 months</p>
          </div>
        </div>
      </div>
    </Container>
  );
};

// ─────────────────── NEW Slide: How the Structured Features Work ───────────────────
// Same role as consolidation's "How the Redraw Strategy Works" — explains how the
// recommended lender's features compound to drop the loan faster.
const FeatureExplainer = ({ on, title, body, accent }) => (
  <div className={`relative rounded-xl border-2 p-4 ${
    on ? 'bg-white border-emerald-300 shadow-sm' : 'bg-slate-50 border-slate-200 opacity-60'
  }`}>
    <div className="flex items-center gap-2 mb-1.5">
      <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold ${on ? `${accent} text-white` : 'bg-slate-300 text-slate-600'}`}>
        {on ? <CheckCircle2 className="h-4 w-4" /> : '·'}
      </div>
      <p className="font-bold text-slate-900">{title}</p>
      {!on && <span className="ml-auto text-[10px] uppercase tracking-wide font-bold text-slate-400">Not active</span>}
    </div>
    <p className="text-sm text-slate-700 leading-relaxed">{body}</p>
  </div>
);

const SlideStructureStrategy = ({ payload, calc }) => {
  const refi = isRefi(payload);
  const rec = calc.recommended;
  const f = rec?.features || {};
  return (
    <Container>
      <Eyebrow dark={false}>The Structure Strategy</Eyebrow>
      <h2 className="text-3xl md:text-5xl font-bold mb-2">{refi ? 'How the new structure compounds.' : 'How the loan structure works for you.'}</h2>
      <p className="text-slate-600 mb-6 text-sm max-w-2xl">
        {rec ? <>The features baked into <strong>{rec.name}</strong>'s loan don't just sit on the brochure — they compound monthly to shave years off the term and thousands off the lifetime interest.</> : 'How the recommended lender\'s features compound monthly.'}
      </p>

      <div className="grid md:grid-cols-3 gap-3 mb-5">
        <FeatureExplainer
          on={!!f.offset}
          title="100% Offset Account"
          accent="bg-emerald-500"
          body="Cash sitting in your offset is treated as a payment against the loan principal — so you pay interest on a smaller balance every day, even though the money is still accessible."
        />
        <FeatureExplainer
          on={!!f.redraw}
          title="Redraw Facility"
          accent="bg-blue-500"
          body="Make extra repayments now, then pull them back later if you need them. Every extra dollar saves interest while it's parked in the loan."
        />
        <FeatureExplainer
          on={!!f.extraPayments}
          title="Unlimited Extra Payments"
          accent="bg-violet-500"
          body="No cap on additional payments — when bonus, tax return, or windfall money lands, it can go straight into the loan without a fee."
        />
      </div>

      <div className="bg-gradient-to-br from-slate-50 to-emerald-50 border border-emerald-200 rounded-2xl p-5">
        <p className="text-[11px] uppercase tracking-wide font-bold text-emerald-700 mb-2">The compounding effect</p>
        <div className="space-y-2 text-sm text-slate-700 leading-relaxed">
          {refi ? (
            <>
              <p>Today you're paying interest on the full <strong>{formatCurrency(payload?.currentLoanBalance)}</strong> at the old rate, every single day.</p>
              <p>Under the new structure, every dollar of savings, salary, and bonus money you park in the offset / redraw is treated as if it's been paid off the loan — so the interest charge shrinks the moment cash lands.</p>
              <p>Result: the lifetime interest paid on this loan is materially lower, and the loan is paid off years earlier with no change to your underlying lifestyle.</p>
            </>
          ) : (
            <>
              <p>From day one, every dollar of your savings, salary or surplus that sits in the offset is treated as if it's already been paid off the loan.</p>
              <p>You pay interest on a smaller daily balance — but the money is still in your account if you need it.</p>
              <p>Pair that with the redraw facility and unlimited extra payments, and the loan compounds <em>down</em> at a rate the rate-sheet alone can't show.</p>
            </>
          )}
        </div>
      </div>
    </Container>
  );
};

// ─────────────────── Slide 2: Funds Required vs Available ───────────────────
const Bar = ({ label, amount, max, color }) => {
  const pct = max ? Math.min(100, (amount / max) * 100) : 0;
  return (
    <div className="mb-3">
      <div className="flex justify-between mb-1 text-sm">
        <span className="font-semibold">{label}</span>
        <span className="font-bold">{formatCurrency(amount)}</span>
      </div>
      <div className="h-2.5 bg-slate-200 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const SlideFunds = ({ payload, calc }) => {
  const fr = payload?.fundsRequired || {};
  const fa = payload?.fundsAvailable || {};
  const refi = isRefi(payload);
  const max = Math.max(calc.totalRequired, calc.totalAvailable, 1);

  const reqRows = [
    refi
      ? { label: 'Current loan balance', value: payload?.currentLoanBalance }
      : { label: 'Purchase price', value: fr.purchasePrice },
    fr.stampDuty && { label: 'Stamp duty', value: fr.stampDuty },
    fr.registration && { label: 'Registration / transfer', value: fr.registration },
    { label: 'Legal / solicitor fees', value: fr.legalFees },
    { label: 'Lender fees', value: fr.lenderFees },
    fr.lmi && { label: 'LMI', value: fr.lmi },
    ...((Array.isArray(fr.otherFees) ? fr.otherFees : []).map((f) => f.amount && ({ label: f.name || 'Other', value: f.amount }))),
  ].filter(Boolean);

  const debtRows = (refi && Array.isArray(payload?.debts)) ? payload.debts.filter((d) => d.amount).map((d) => ({
    label: `${d.type || 'Debt'}${d.name ? ` — ${d.name}` : ''}`,
    value: d.amount,
  })) : [];

  const avlRows = [
    { label: 'Loan amount', value: fa.loanAmount },
    fa.deposit && { label: 'Deposit', value: fa.deposit },
    fa.gift && { label: 'Gift', value: fa.gift },
    fa.fhog && { label: 'First Home Owner Grant', value: fa.fhog },
    fa.otherContributions && { label: 'Other contributions', value: fa.otherContributions },
  ].filter(Boolean);

  return (
    <Container>
      <Eyebrow dark={false}>Funds Breakdown</Eyebrow>
      <h2 className="text-3xl md:text-5xl font-bold mb-6">{refi ? 'What gets refinanced.' : 'Where the money goes.'}</h2>

      <div className="grid md:grid-cols-2 gap-5">
        {/* Required */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
          <p className="text-[11px] uppercase tracking-wide font-bold text-rose-600 mb-2">Funds Required</p>
          <Bar label="Total" amount={calc.totalRequired} max={max} color="bg-rose-500" />
          <table className="w-full text-sm mt-3">
            <tbody>
              {reqRows.map((row, i) => (
                <tr key={`r-${i}`} className="border-b border-slate-100">
                  <td className="py-1.5 text-slate-700">{row.label}</td>
                  <td className="py-1.5 text-right font-medium">{formatCurrency(row.value)}</td>
                </tr>
              ))}
              {debtRows.length > 0 && (
                <>
                  <tr><td colSpan={2} className="pt-3 pb-1 text-[11px] uppercase tracking-wide font-bold text-slate-500">Debts being consolidated</td></tr>
                  {debtRows.map((row, i) => (
                    <tr key={`d-${i}`} className="border-b border-slate-100">
                      <td className="py-1.5 text-slate-700">{row.label}</td>
                      <td className="py-1.5 text-right font-medium">{formatCurrency(row.value)}</td>
                    </tr>
                  ))}
                </>
              )}
              <tr className="font-bold text-slate-900">
                <td className="py-2">Total required</td>
                <td className="py-2 text-right text-rose-600">{formatCurrency(calc.totalRequired)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        {/* Available */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
          <p className="text-[11px] uppercase tracking-wide font-bold text-emerald-600 mb-2">Funds Available</p>
          <Bar label="Total" amount={calc.totalAvailable} max={max} color="bg-emerald-500" />
          <table className="w-full text-sm mt-3">
            <tbody>
              {avlRows.map((row, i) => (
                <tr key={`a-${i}`} className="border-b border-slate-100">
                  <td className="py-1.5 text-slate-700">{row.label}</td>
                  <td className="py-1.5 text-right font-medium">{formatCurrency(row.value)}</td>
                </tr>
              ))}
              <tr className="font-bold text-slate-900">
                <td className="py-2">Total available</td>
                <td className="py-2 text-right text-emerald-600">{formatCurrency(calc.totalAvailable)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className={`mt-5 rounded-xl p-4 flex items-start gap-2 text-sm font-semibold ${
        calc.difference >= 0
          ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
          : 'bg-rose-50 border border-rose-200 text-rose-800'
      }`}>
        {calc.difference >= 0 ? <CheckCircle2 className="h-5 w-5 mt-0.5" /> : <AlertTriangle className="h-5 w-5 mt-0.5" />}
        <span>
          {calc.difference >= 0
            ? <>Surplus of <strong>{formatCurrency(calc.difference)}</strong> — funds adequately cover settlement.</>
            : <>Shortfall of <strong>{formatCurrency(Math.abs(calc.difference))}</strong> — additional contribution required before settlement.</>
          }
        </span>
      </div>
    </Container>
  );
};

// ─────────────────── Slide 3: Lender Comparison ───────────────────
const featureBadges = (features) => {
  if (!features || typeof features !== 'object') return [];
  const out = [];
  if (features.offset) out.push('Offset');
  if (features.redraw) out.push('Redraw');
  if (features.extraPayments) out.push('Extra Payments');
  return out;
};

const SlideLenders = ({ payload, calc }) => {
  const lenders = Array.isArray(payload?.lenders) ? payload.lenders : [];
  return (
    <Container dark>
      <Eyebrow>Lender Options</Eyebrow>
      <h2 className="text-3xl md:text-5xl font-bold mb-6">Three options. One we recommend.</h2>

      <div className="grid md:grid-cols-3 gap-3">
        {lenders.map((l, i) => {
          const isRec = l.recommended || i === calc.recommendedIndex;
          const r = calc.lenderRepayments[i] || {};
          return (
            <div
              key={i}
              className={`relative rounded-2xl p-5 border-2 ${
                isRec
                  ? 'bg-emerald-500/10 border-emerald-400 shadow-lg shadow-emerald-500/20'
                  : 'bg-white/5 border-white/10'
              }`}
            >
              {isRec && (
                <div className="absolute -top-3 left-4 inline-flex items-center gap-1 bg-emerald-500 text-white text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded">
                  <Star className="h-3 w-3" /> Top Pick
                </div>
              )}
              <p className="text-base font-bold">{l.name || `Option ${i + 1}`}</p>
              <p className={`text-3xl font-bold mt-1 ${isRec ? 'text-emerald-300' : 'text-white'}`}>{l.rate ? Number(l.rate).toFixed(2) : '—'}<span className="text-base font-normal">%</span></p>
              <p className="text-xs text-slate-300 mt-1">{l.type || 'Variable'} · {l.repayment || 'P&I'}</p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {featureBadges(l.features).map((b) => (
                  <span key={b} className="text-[10px] bg-white/10 border border-white/15 rounded px-1.5 py-0.5 uppercase tracking-wide font-bold">{b}</span>
                ))}
                {featureBadges(l.features).length === 0 && (
                  <span className="text-[10px] text-slate-500 italic">No features specified</span>
                )}
              </div>
              <div className="mt-4 pt-3 border-t border-white/10 text-xs">
                <p className="text-slate-400">Monthly</p>
                <p className="text-lg font-bold">{formatCurrency(r.monthly)}</p>
              </div>
            </div>
          );
        })}
      </div>
    </Container>
  );
};

// ─────────────────── Slide 4: Recommendation + Repayments ───────────────────
const HeroTile = ({ label, value, sub, accentRing, pulse = false }) => (
  <div className={`relative bg-white rounded-2xl border-2 ${accentRing} p-5 shadow-sm ${pulse ? 'proposal-pulse-glow' : ''}`}>
    {pulse && <div className="absolute inset-0 rounded-2xl proposal-shimmer pointer-events-none" />}
    <p className="text-[11px] uppercase tracking-wide font-bold text-slate-500 relative">{label}</p>
    <p className={`text-3xl md:text-4xl font-bold text-slate-900 mt-2 relative ${pulse ? 'proposal-pulse-number' : ''}`}>{value}</p>
    {sub && <p className="text-xs text-slate-500 mt-1 relative">{sub}</p>}
  </div>
);

const SlideRecommendation = ({ payload, calc }) => {
  const rec = calc.recommended;
  const lenders = Array.isArray(payload?.lenders) ? payload.lenders : [];
  const recRate = rec ? Number(rec.rate || 0) : 0;
  // Computed "savings" vs the most expensive lender — useful colour for the recommendation
  const maxRate = lenders.length
    ? Math.max(...lenders.map((l) => Number(l.rate) || 0))
    : recRate;
  const rateDelta = Math.max(0, maxRate - recRate);
  return (
    <Container>
      <Eyebrow dark={false}>Our Recommendation</Eyebrow>
      <h2 className="text-3xl md:text-5xl font-bold mb-2">{rec ? rec.name : 'Top pick'}.</h2>
      <p className="text-slate-600 mb-6 max-w-xl">
        {rec ? <>At <strong>{recRate.toFixed(2)}%</strong> ({rec.type || 'Variable'} · {rec.repayment || 'P&I'}), this option gives you the strongest combination of rate, structure and features for your situation{rateDelta > 0 ? <> — a <strong className="text-emerald-700">{rateDelta.toFixed(2)}%</strong> saving vs the highest-rate option you compared.</> : '.'}</> : 'No lender flagged as recommended yet.'}
      </p>

      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <HeroTile label="Weekly" value={formatCurrency(calc.repayments.weekly)} sub="Recommended lender" accentRing="border-emerald-300" pulse />
        <HeroTile label="Fortnightly" value={formatCurrency(calc.repayments.fortnightly)} sub="Recommended lender" accentRing="border-blue-300" pulse />
        <HeroTile label="Monthly" value={formatCurrency(calc.repayments.monthly)} sub="Recommended lender" accentRing="border-violet-300" pulse />
      </div>

      {/* Side-by-side repayment comparison across all 3 lenders */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden border border-slate-200">
        <div className="bg-slate-900 text-white px-4 py-2 text-sm font-semibold uppercase tracking-wide">Side-by-side repayments</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-slate-50">
                <th className="text-left py-2 px-3 font-semibold text-slate-600">Lender</th>
                <th className="text-right py-2 px-3 font-semibold text-slate-600">Rate</th>
                <th className="text-right py-2 px-3 font-semibold text-slate-600">Weekly</th>
                <th className="text-right py-2 px-3 font-semibold text-slate-600">Fortnightly</th>
                <th className="text-right py-2 px-3 font-semibold text-slate-600">Monthly</th>
              </tr>
            </thead>
            <tbody>
              {(calc.lenderRepayments || []).map((l, i) => {
                const isRec = l.recommended || i === calc.recommendedIndex;
                return (
                  <tr key={i} className={`border-b ${isRec ? 'bg-emerald-50/60' : ''}`}>
                    <td className={`py-2 px-3 font-medium ${isRec ? 'text-emerald-800' : ''}`}>
                      {l.name || `Option ${i + 1}`}
                      {isRec && <span className="ml-2 inline-flex items-center gap-1 bg-emerald-600 text-white text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded font-bold"><Star className="h-2.5 w-2.5" />Top pick</span>}
                    </td>
                    <td className={`py-2 px-3 text-right ${isRec ? 'text-emerald-700 font-bold' : 'text-slate-700'}`}>{Number(l.rate || 0).toFixed(2)}%</td>
                    <td className={`py-2 px-3 text-right ${isRec ? 'text-emerald-700 font-bold' : 'text-slate-700'}`}>{formatCurrency(l.weekly)}</td>
                    <td className={`py-2 px-3 text-right ${isRec ? 'text-emerald-700 font-bold' : 'text-slate-700'}`}>{formatCurrency(l.fortnightly)}</td>
                    <td className={`py-2 px-3 text-right ${isRec ? 'text-emerald-700 font-bold' : 'text-slate-700'}`}>{formatCurrency(l.monthly)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-xl p-3 mt-4 flex items-start gap-2 text-sm">
        <Wallet className="h-4 w-4 flex-shrink-0 mt-0.5" />
        <p>
          Repayments are estimated using {formatCurrency(calc.loanAmount)} at {recRate.toFixed(2)}% over 360 months. Actual repayments will be confirmed by the lender on formal approval.
        </p>
      </div>
    </Container>
  );
};

// ─────────────────── Slide 5: Accept ───────────────────
const SlideAccept = ({ payload, calc, accepted, onAccept }) => (
  <Container>
    <Eyebrow dark={false}>What happens next</Eyebrow>
    <h2 className="text-3xl md:text-5xl font-bold mb-3">Ready to proceed?</h2>
    <p className="text-slate-600 mb-6 max-w-xl">
      If you're happy with the structure, hit <strong>Accept Proposal</strong> below. Your broker will move the application into formal credit assessment with the recommended lender.
    </p>

    <div className="bg-white rounded-2xl border-2 border-emerald-300 shadow-md p-5 mb-6 proposal-pulse-glow relative overflow-hidden">
      <div className="absolute inset-0 proposal-shimmer pointer-events-none" />
      <p className="text-[11px] uppercase tracking-wide font-bold text-emerald-700 mb-1 relative">YOUR HEADLINE OUTCOME</p>
      <p className="text-3xl font-bold text-slate-900 relative proposal-pulse-number">
        {calc.recommended ? calc.recommended.name : 'Recommended lender'} · {formatCurrency(calc.repayments.monthly)}/mo
      </p>
      <p className="text-sm text-slate-600 mt-1 relative">
        {calc.difference >= 0
          ? <>Funds Required {formatCurrency(calc.totalRequired)} · Funds Available {formatCurrency(calc.totalAvailable)} (surplus {formatCurrency(calc.difference)}).</>
          : <>Funds Required {formatCurrency(calc.totalRequired)} · Funds Available {formatCurrency(calc.totalAvailable)} (shortfall {formatCurrency(Math.abs(calc.difference))} to top up).</>
        }
      </p>
    </div>

    {accepted ? (
      <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-300 text-emerald-800 rounded-xl p-4">
        <Award className="h-6 w-6" />
        <div>
          <p className="font-bold">Proposal accepted ✅</p>
          <p className="text-sm">Your broker will be in touch shortly to start the formal application.</p>
        </div>
      </div>
    ) : (
      <Button
        onClick={onAccept}
        data-testid="proposal-accept-btn"
        className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-6 text-lg shadow-lg"
      >
        <CheckCircle2 className="h-5 w-5 mr-2" /> Accept Proposal
      </Button>
    )}

    <p className="text-[10px] text-slate-500 mt-6 max-w-2xl leading-relaxed">
      Please do not rely on this report as financial or credit advice, as the figures and information provided are estimates only. This information is general in nature and should be considered in conjunction with the NCCP requirements, your full application details, and the formal loan and credit documentation provided by the lender should you decide to proceed further.
    </p>
  </Container>
);

// ─────────────────── Slide registry ───────────────────
// 8-slide deck mirroring the consolidation deck's depth — adapts content
// based on `payload.loanPurpose` ('purchase' | 'refinance').
export const BROKER_PROPOSAL_SLIDES = [
  { key: 'cover', title: 'Cover', component: SlideCover },
  { key: 'current', title: 'Current Situation', component: SlideCurrentSituation },
  { key: 'proposal', title: "What We're Proposing", component: SlideProposalIntro },
  { key: 'funds', title: 'Funds Breakdown', component: SlideFunds },
  { key: 'lenders', title: 'Lender Options', component: SlideLenders },
  { key: 'recommendation', title: 'Recommendation', component: SlideRecommendation },
  { key: 'strategy', title: 'Structure Strategy', component: SlideStructureStrategy },
  { key: 'accept', title: 'Accept', component: SlideAccept },
];

export const renderBrokerProposalSlide = ({ index, payload, clientName, brokerName, readonly, accepted, onAccept }) => {
  const slide = BROKER_PROPOSAL_SLIDES[index] || BROKER_PROPOSAL_SLIDES[0];
  const SlideComp = slide.component;
  const calc = computeBrokerProposal(payload || {});
  return (
    <SlideComp
      payload={payload}
      calc={calc}
      clientName={clientName}
      brokerName={brokerName}
      readonly={readonly}
      accepted={accepted}
      onAccept={onAccept}
    />
  );
};

export default BROKER_PROPOSAL_SLIDES;
